<template>
  <div class="is-loaded">
    <form class="c-form c-form--bordered c-form--online-payment" novalidate="" @submit.prevent="onSubmit">
      <div class="c-form__header">
        <div class="u-text-large u-text-strong">{{ t('Start your payment') }}</div>
        <div>{{ t('Fill in your details below.') }}</div>
      </div>
      <div class="c-form__body">
        <div class="c-form__column">
          <div class="c-control-group" v-show="errors.gateway.length > 0">
            <div class="c-help-block">
              <div v-for="error in errors.gateway" v-text="error"></div>
            </div>
          </div>

          <div class="c-control-group" v-if="cardsImageUrl">
            <strong class="u-h4 u-my-0">{{ t('We accept') }}:</strong>
            <img :src="cardsImageUrl" alt="" class="u-image-adaptive u-my-2"/>
          </div>

          <div class="c-control-group" v-show="paymentChoose">
            <strong class="u-h4 u-mt-0 u-mb-4">{{ t('Select payment method') }}:</strong>
            <div class="c-radio-group">
              <div class="c-radio c-radio--large">
                <label class="c-radio__label">
                  <input type="radio" name="payment_type" v-model="selectedPaymentType" :class="{'active': selectedPaymentType === PAYMENT_TYPES.BANKING}" class="c-radio__input" :value="PAYMENT_TYPES.BANKING">
                  <span class="c-radio__icon"></span>
                  <span class="c-radio__text">{{ t('Home Banking') }}</span>
                </label>
              </div>
              <div class="c-radio c-radio--large">
                <label class="c-radio__label">
                  <input type="radio" name="payment_type" v-model="selectedPaymentType" :class="{'active': selectedPaymentType === PAYMENT_TYPES.CARD}" class="c-radio__input" :value="PAYMENT_TYPES.CARD">
                  <span class="c-radio__icon"></span>
                  <span class="c-radio__text">{{ t('Pay with Card') }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="c-control-group" v-show="cardsChoose && additionalFee > 0 && selectedPaymentType === PAYMENT_TYPES.CARD">
            <div class="c-radio-group">
              <div class="c-radio c-radio--large">
                <label class="c-radio__label">
                  <input type="radio" name="card_type" v-model="selectedCardType" class="c-radio__input" :class="{'active': selectedCardType === CARD_TYPES.CREDIT}" :value="CARD_TYPES.CREDIT">
                  <span class="c-radio__icon"></span>
                  <span class="c-radio__text">{{ creditCardLabel }}</span>
                </label>
              </div>
              <div class="c-radio c-radio--large">
                <label class="c-radio__label">
                  <input type="radio" name="card_type" v-model="selectedCardType" class="c-radio__input" :class="{'active': selectedCardType === CARD_TYPES.DEBIT}" :value="CARD_TYPES.DEBIT">
                  <span class="c-radio__icon"></span>
                  <span class="c-radio__text">{{ debitCardLabel }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="c-control-group">
            <label class="c-control-label">{{ t('Payment amount in GBP') }}: <span class="u-text-danger">*</span></label>
            <input type="text" class="c-input" name="amount" v-model="amount" placeholder="0.00" autocomplete="off">
          </div>

          <div class="c-control-group" v-show="showFee">
            <label class="c-control-label">{{ t('Total amount') }} ({{ t('Credit card fee') }} {{ additionalFee }}%):</label>
            <input type="text" class="c-input" :value="total" name="total-amount">
          </div>

          <div class="c-control-group">
            <label class="c-control-label">{{ t('Invoice or quote reference no.') }}: <span class="u-text-danger">*</span></label>
            <input type="text" class="c-input" :class="{'has-error': errors.invoice.length > 0}" name="invoice" v-model="invoice" placeholder="123456" autocomplete="off">
            <div class="c-help-block" v-show="errors.invoice.length > 0">
              <div v-for="error in errors.invoice" v-text="error"></div>
            </div>
          </div>

          <div class="c-control-group">
            <label class="c-control-label">{{ t('Name') }}: <span class="u-text-danger">*</span></label>
            <input type="text" class="c-input" :class="{'has-error': errors.person.length > 0}" autocomplete="name" name="name" v-model="name">
            <div class="c-help-block" v-show="errors.person.length > 0">
              <div v-for="error in errors.person" v-text="error"></div>
            </div>
          </div>

          <div class="c-control-group u-mb-0">
            <button type="submit" :disabled="hasErrors" class="c-button c-button--primary c-button--block c-button--large u-text-uppercase u-px-2">
              {{ t('Continue', 'payment') }}
            </button>
          </div>
        </div>
      </div>
    </form>
    <modal ref="paymentGateway"></modal>
  </div>
</template>
<script lang="ts">
import accounting from 'accounting';
import { Component, Prop } from 'vue-property-decorator';
import { formatter } from '../app.config';
import Modal from './AltapayModal.vue';
import BaseComponent from './Base';

@Component({
  components: {
    Modal,
  },
})
export default class Altapay extends BaseComponent {
  public readonly $refs: {
    paymentGateway: any,
  };

  @Prop({type: Number, required: true}) protected readonly additionalFee: number;
  @Prop({type: Boolean, default: false}) protected readonly paymentChoose: boolean;
  @Prop({type: Boolean, default: false}) protected readonly cardsChoose: boolean;
  @Prop({type: String}) protected readonly cardsImageUrl: string;
  @Prop({type: String}) protected readonly creditCardLabel: string;
  @Prop({type: String}) protected readonly debitCardLabel: string;
  @Prop({type: String}) protected readonly successUrl: string;
  @Prop({type: String}) protected readonly cancelUrl: string;

  protected PAYMENT_TYPES: { BANKING: string, CARD: string } = {
    BANKING: 'banking',
    CARD: 'card',
  };

  protected CARD_TYPES: { DEBIT: string, CREDIT: string } = {
    DEBIT: 'debit',
    CREDIT: 'credit',
  };

  protected lastValidAmount: string = '';
  protected cardType: string = this.CARD_TYPES.CREDIT;
  protected paymentType: string = this.PAYMENT_TYPES.BANKING;
  protected showFee: boolean = false;
  protected innerAmount: string = '';

  protected get selectedCardType(): string {
    return this.cardType;
  }

  protected set selectedCardType(type: string) {
    this.cardType = type;
    this.updateAmount(this.innerAmount);
  }

  protected get selectedPaymentType(): string {
    return this.paymentType;
  }

  protected set selectedPaymentType(type: string) {
    this.paymentType = type;

    this.updateAmount(this.innerAmount);
  }

  protected transaction: { amount: number, total: number, invoice: string, person: string, comment: string } = {
    amount: 0,
    total: 0,
    invoice: '',
    person: '',
    comment: '',
  };

  protected errors: { amount: string[], invoice: string[], person: string[], gateway: string[] } = {
    amount: [],
    invoice: [],
    person: [],
    gateway: [],
  };

  protected get name(): string {
    return this.transaction.person;
  }

  protected set name(name: string) {
    if (name.length !== 0) {
      this.hideError('person');
      this.transaction.person = name;
    } else {
      this.transaction.person = '';
      this.addError('person', this.t('Please fill in the name'));
    }
  }

  protected get invoice(): string {
    return this.transaction.invoice;
  }

  protected set invoice(invoice: string) {
    this.transaction.invoice = invoice;

    if (invoice === '') {
      this.addError('invoice', this.t('Please fill in invoice or quote reference no. If you have not yet received an invoice or quotation number, simply enter " 123456 " into the required field.'));
    } else if (!/[1-9]+/.test(invoice)) {
      this.addError('invoice', this.t('Invoice number must be at least 4 digits. If you have not yet received an invoice or quotation number, simply enter " 123456 " into the required field.'));
    } else {
      this.hideError('invoice');
      this.transaction.invoice = invoice;
    }
  }

  protected get amount(): string {
    return this.innerAmount;
  }

  protected set amount(amount: string) {
    if (this.isValidAmount(amount)) {
      this.innerAmount = amount;
      this.lastValidAmount = amount;
      this.updateAmount(amount);
    } else {
      // to trigger v-model update
      this.innerAmount = amount;
      this.innerAmount = this.lastValidAmount;
    }
  }

  protected isValidAmount(amount: string): boolean {
    // 1-8 digits optional, dot optional, 1-2 digits
    return /^$|^\d{0,8}(\.\d{0,2})?\.?$/.test(amount);
  }

  protected updateAmount(amount: string): void {
    let parsedAmount: number | any = amount.trim().replace(/,/, '.');
    if (parsedAmount === '') {
      parsedAmount = 0;
      this.addError('amount', this.t('Please fill in the amount'));
    } else if (!isNaN(parseFloat(parsedAmount))) {
      parsedAmount = parseFloat(parsedAmount);
      this.hideError('amount');
    }

    this.showFee = false;
    if (this.additionalFee > 0 && this.cardType !== this.CARD_TYPES.DEBIT) {
      this.transaction.total = parsedAmount + parsedAmount * (this.additionalFee / 100);
      if (this.transaction.total > 0 && this.additionalFee > 0) {
        this.showFee = true;
      }
    } else {
      this.transaction.total = parsedAmount;
    }

    this.transaction.amount = parsedAmount;
  }

  protected get comment(): string {
    return this.transaction.comment;
  }

  protected set comment(comment: string) {
    this.transaction.comment = comment || '';
  }

  protected get total(): string {
    return formatter.asDecimal(Math.ceil(this.transaction.total * 100) / 100);
  }

  // start processing
  protected start(): void {
    const { firstName, lastName } = this.parsePerson(this.transaction.person);
    const amount = Math.ceil(this.transaction.total * 100);
    const orderId = this.transaction.invoice;
    const description = {
      invoice: `Invoice or quote reference no: ${orderId}`,
      amount: `Amount ex. fee: ${accounting.formatNumber(this.transaction.amount, 2, '', ',')}`,
      firstName,
      lastName,
      comment: this.transaction.comment ? `${this.transaction.comment}` : ``,
    };

    try {
      this.$refs.paymentGateway.open(amount, orderId, null, description);
    } catch {
      this.addError('gateway', this.t('Sorry, we have a problem with payment gateway. Please try later.'));
    }
  }

  protected parsePerson(person: string): { firstName: string; lastName: string } {
    const personArr = person
        .split(' ')
        .filter((item) => item.length > 1);

    let firstName = '';
    let lastName = '';

    if (personArr[0] && personArr[0].length) {
      firstName = personArr[0];
    }

    if (personArr[1] && personArr[1].length) {
      lastName = personArr[1];
    }

    if (personArr[2] && personArr[2].length) {
      lastName = `${lastName} ${personArr.slice(2).join(' ')}`;
    }

    return {
      firstName,
      lastName,
    };
  }

  protected addError(key: string, error: string): void {
    this.$set(this.errors, key, [error]);
  }

  protected hideError(key: string): void {
    this.$set(this.errors, key, []);
  }

  protected get hasErrors(): boolean {
    return !!Object.keys(this.errors).find((key) => this.errors[key].length > 0);
  }

  protected onSubmit(): void {
    // trigger setters
    this.invoice = this.invoice.trim();
    this.name = this.name.trim();
    this.comment = this.comment.trim();

    if (!this.hasErrors) {
      this.start();
    }
  }
}
</script>
